import { creatureText } from '../../shared/game-config.ts';
import type { Move } from '../../shared/moves.ts';
import type { MoveOffer, OwnedMonsterView } from '../../shared/protocol.ts';
import { $, el } from '../common/dom.ts';
import { withIcon } from '../common/icons.ts';
import { Modal } from '../common/modal.ts';
import { moveCard } from './move-card.ts';

export interface MoveLearnHandlers {
  /** Nouvelles attaques du monstre (la nouvelle à la place de celle oubliée). */
  onLearn(monsterId: number, moveIds: number[]): void;
}

/**
 * Après un combat : un monstre qui connaît déjà toutes ses attaques veut en apprendre une nouvelle. Le joueur en oublie
 * une pour la remplacer, ou passe (il pourra toujours la lui donner depuis sa fiche). Une proposition après l'autre ;
 * fermer la fenêtre passe toutes celles qui restent.
 */
export class MoveLearnDialog extends Modal {
  private readonly handlers: MoveLearnHandlers;
  private readonly body = $('.move-learn-body', this.root);
  private catalog = new Map<number, Move>();
  private collection = new Map<number, OwnedMonsterView>();
  private queue: MoveOffer[] = [];

  constructor(handlers: MoveLearnHandlers) {
    super('#move-learn');
    this.handlers = handlers;
  }

  setMoveCatalog(catalog: Map<number, Move>): void {
    this.catalog = catalog;
  }

  setCollection(collection: readonly OwnedMonsterView[]): void {
    this.collection = new Map(collection.map((monster) => [monster.id, monster]));
  }

  /** Propose les attaques à apprendre, une à une (rien si la liste est vide). */
  offer(offers: readonly MoveOffer[]): void {
    this.queue = [...offers];
    this.next();
  }

  override close(): void {
    this.queue = [];
    super.close();
  }

  private next(): void {
    // Une attaque que le monstre connaît déjà (apprise entre-temps depuis sa fiche) n'est plus proposée.
    while (this.queue.length) {
      const offer = this.queue[0]!;
      const monster = this.collection.get(offer.monsterId);
      const move = this.catalog.get(offer.moveId);
      if (monster && move && !monster.moves.includes(move.id)) return this.show(monster, move);
      this.queue.shift();
    }
    super.close();
  }

  private show(monster: OwnedMonsterView, move: Move): void {
    const forget = monster.moves.map((id) => {
      const known = this.catalog.get(id);
      if (!known) return el('span');
      const card = moveCard(known, false, () => this.learn(monster, id, move.id));
      card.classList.add('forget');
      card.title = `Oublier ${known.name} pour apprendre ${move.name}`;
      return card;
    });
    const skip = el('button', { className: 'secondary', title: creatureText('Garder ses attaques : vous pourrez lui donner celle-ci plus tard, depuis sa fiche (Équipe).') },
      ...withIcon('close', 'Passer'));
    skip.addEventListener('click', () => this.skip());
    this.body.replaceChildren(
      el('p', { className: 'move-learn-intro' }, el('strong', { textContent: monster.name }), ' veut apprendre ', el('strong', { textContent: move.name }), ' !'),
      el('div', { className: 'move-learn-new' }, moveCard(move, true)),
      el('p', { className: 'muted', textContent: `${monster.name} connaît déjà ${monster.moves.length} attaques. Laquelle oublier ?` }),
      el('div', { className: 'move-grid' }, ...forget),
      el('div', { className: 'move-learn-actions' },
        el('small', { className: 'muted', textContent: creatureText('Passer : vous pourrez la lui apprendre plus tard depuis sa fiche.') }), skip));
    this.open();
  }

  private learn(monster: OwnedMonsterView, forgotten: number, learned: number): void {
    const moves = monster.moves.map((id) => (id === forgotten ? learned : id));
    this.handlers.onLearn(monster.id, moves);
    // La collection à jour arrive du serveur ; d'ici là, la suite des propositions part de ce choix.
    this.collection.set(monster.id, { ...monster, moves });
    this.queue.shift();
    this.next();
  }

  private skip(): void {
    this.queue.shift();
    this.next();
  }
}
